import { useCallback, useMemo } from "react";

import { getBreakGlassReassignParams } from "../identityPolicyConfirmations";

import { useBreakGlassOwnersColumns } from "./BreakGlass/useBreakGlassOwnersColumns";
import { computeCompactDataTableHeight } from "./compactDataTableHeight";
import { SectionShell } from "./SectionShell";

import { DataTable } from "@/components/ui/ag-grid-table";
import { TableColumn } from "@/components/ui/TableV2";
import { ToastType } from "@/enums";
import { OrganizationMemberLifecycleStatus } from "@/enums/organization-member-lifecycle-status";
import { useConfirm } from "@/context/confirm-context";
import { useActiveOrgMembers } from "@/queries/organization-members";
import { useSetBreakGlass } from "@/queries/identity/useSetBreakGlass";
import { isOrganizationMembersMemberRow } from "@/types/organization-member";
import { displayToast } from "@/utils/toast";

interface Props {
  organizationId: number;
  onError: (msg: string | null) => void;
}

const BREAK_GLASS_ROW_HEIGHT = 56;

const EMERGENCY_ACCESS_HELP =
  "Exactly one Owner holds emergency access at a time. Select another Owner to reassign — it cannot be removed while enterprise identity is on. If none is assigned, PromptLayer auto-selects an Owner. The designated Owner must complete MFA and an emergency password in Account Settings before enabling Require SSO.";

/** Org Owners eligible for the single emergency access slot (radio reassignment or auto-assign). */
export const BreakGlassOwnersSection = ({ organizationId, onError }: Props) => {
  const { data: membersData, isLoading } = useActiveOrgMembers({
    organization_id: organizationId,
    perPage: 250,
  });
  const setBreakGlass = useSetBreakGlass(organizationId);
  const { confirm } = useConfirm();

  const owners = useMemo(
    () =>
      (membersData?.items ?? [])
        .filter(isOrganizationMembersMemberRow)
        .filter(item => item.status === OrganizationMemberLifecycleStatus.ACTIVE && item.is_owner),
    [membersData],
  );

  const handleAssign = useCallback(
    async (userId: number, ownerEmail: string) => {
      const current = owners.find(owner => owner.user_id === userId);
      if (!current || current.is_break_glass) {
        return;
      }

      const confirmed = await confirm(getBreakGlassReassignParams(ownerEmail));
      if (!confirmed) {
        return;
      }

      onError(null);
      setBreakGlass.mutate(
        { target_user_id: userId, is_break_glass: true },
        {
          onSuccess: () => {
            displayToast("Emergency access reassigned.", ToastType.success);
          },
          onError: () => onError("Failed to reassign emergency access."),
        },
      );
    },
    [confirm, onError, owners, setBreakGlass],
  );

  const { columns } = useBreakGlassOwnersColumns({
    onAssign: handleAssign,
    isUpdating: setBreakGlass.isPending,
  });

  const tableHeightPx = computeCompactDataTableHeight({
    rowCount: owners.length,
    rowHeight: BREAK_GLASS_ROW_HEIGHT,
  });

  return (
    <SectionShell
      title="Emergency access owner"
      description="One organization Owner keeps email/password login and native MFA when Require SSO is enabled."
      helpTooltip={EMERGENCY_ACCESS_HELP}
    >
      <div className="w-full overflow-hidden" style={{ height: tableHeightPx }}>
        <DataTable
          data={isLoading ? [] : owners}
          columns={columns as TableColumn<(typeof owners)[number]>[]}
          isLoading={isLoading}
          height="100%"
          rowHeight={BREAK_GLASS_ROW_HEIGHT}
          getRowHeight={() => BREAK_GLASS_ROW_HEIGHT}
          hoverable
          enableSorting={false}
          emptyStateMessage="No organization Owners found."
          gridOptions={{
            suppressHorizontalScroll: false,
            alwaysShowHorizontalScroll: false,
            suppressScrollOnNewData: true,
          }}
          className="[&_.ag-cell-value]:justify-start [&_.ag-header-cell-label]:justify-start [&_.ag-header-cell-text]:text-left"
        />
      </div>
    </SectionShell>
  );
};
